import { generateChartColors } from '../../services/analysisService';

/** One exam's score in a series; `absent` marks a paper the student missed. */
export interface TrendPoint {
  examId: string;
  examName: string;
  examDate: string;
  value: number | null;
  absent?: boolean;
}

/** A line on a trend chart: one subject, or the overall score. */
export interface TrendSeries {
  id: string;
  name: string;
  color: string;
  overall?: boolean;
  points: TrendPoint[];
}

/** One exam on the x-axis: `values[seriesId]` is that series' score. */
export interface TrendRow {
  examId: string;
  examName: string;
  examDate: string;
  values: Record<string, number | null>;
  /** Names of the series the student was absent for. */
  absent: string[];
}

export const OVERALL_ID = '__overall';

/** Gives each subject a stable colour, then adds the overall series last. */
export const withColors = (
  subjects: Array<Omit<TrendSeries, 'color'>>,
  overall: Omit<TrendSeries, 'color' | 'id' | 'overall'> | null,
  overallColor: string,
): TrendSeries[] => {
  const colors = generateChartColors(subjects.length);
  const out: TrendSeries[] = subjects.map((s, i) => ({ ...s, color: colors[i] }));
  if (overall) out.push({ ...overall, id: OVERALL_ID, overall: true, color: overallColor });
  return out;
};

/** Pivots series into one row per exam, oldest first. */
export const toRows = (series: TrendSeries[]): TrendRow[] => {
  const rows = new Map<string, TrendRow>();
  series.forEach((s) => s.points.forEach((p) => {
    let row = rows.get(p.examId);
    if (!row) {
      row = { examId: p.examId, examName: p.examName, examDate: p.examDate, values: {}, absent: [] };
      rows.set(p.examId, row);
    }
    row.values[s.id] = p.absent ? null : p.value;
    if (p.absent) row.absent.push(s.name);
  }));
  return Array.from(rows.values()).sort(
    (a, b) => new Date(a.examDate).getTime() - new Date(b.examDate).getTime(),
  );
};

/** Mean of the non-null numbers, or null when there are none. */
export const mean = (values: Array<number | null | undefined>): number | null => {
  const present = values.filter((v): v is number => v != null);
  return present.length ? present.reduce((a, b) => a + b, 0) / present.length : null;
};

export interface TrendSummary {
  latest: { row: TrendRow; value: number } | null;
  /** Change in the overall score since the exam before the latest. */
  delta: number | null;
  best: { series: TrendSeries; value: number } | null;
  worst: { series: TrendSeries; value: number } | null;
}

/** The headline numbers of a trend: latest overall, its change, strongest and weakest subject. */
export const summarise = (rows: TrendRow[], series: TrendSeries[]): TrendSummary => {
  const scored = rows.filter((r) => r.values[OVERALL_ID] != null);
  const last = scored[scored.length - 1];
  const prev = scored[scored.length - 2];
  const averages = series
    .filter((s) => !s.overall)
    .map((s) => ({ series: s, value: mean(rows.map((r) => r.values[s.id])) }))
    .filter((a): a is { series: TrendSeries; value: number } => a.value != null)
    .sort((a, b) => b.value - a.value);
  return {
    latest: last ? { row: last, value: last.values[OVERALL_ID] as number } : null,
    delta: last && prev ? (last.values[OVERALL_ID] as number) - (prev.values[OVERALL_ID] as number) : null,
    best: averages[0] ?? null,
    worst: averages.length > 1 ? averages[averages.length - 1] : null,
  };
};

export const pct = (v: number | null | undefined, digits = 1) => (v == null ? '—' : `${v.toFixed(digits)}%`);
